'use client';

import { useMemo, useRef } from 'react';
import { SpotBalanceType } from '@drift-labs/sdk';
import { matchEnum } from '@drift/common';
import useDriftAccountStore, {
	AccountSpotBalance,
	checkIfAccountsSpotBalancesAreEqual,
} from 'src/stores/useDriftAccountsStore';
import useUiUpdateInterval from './useUiUpdateInterval';

const EMPTY_BALANCES: AccountSpotBalance[] = [];

const useAccountSpotBalances = (
	userKey: string | undefined,
	balanceType?: SpotBalanceType | null,
	poolId?: number
): AccountSpotBalance[] => {
	const userAccountBalances = useDriftAccountStore((s) =>
		userKey
			? (s.accounts[userKey]?.spotBalances ?? EMPTY_BALANCES)
			: EMPTY_BALANCES
	);

	return useMemo(() => {
		return userAccountBalances
			.filter((balance: AccountSpotBalance) =>
				balanceType ? matchEnum(balance.balanceType, balanceType) : true
			)
			.filter((balance: AccountSpotBalance) =>
				poolId !== undefined ? balance.asset.poolId === poolId : true
			);
	}, [userAccountBalances, balanceType, poolId]);
};

export const useAggregateAccountsSpotBalances = (
	balanceType?: SpotBalanceType
) => {
	const getAggregateAccountsSpotBalances = useDriftAccountStore(
		(s) => s.getAggregateAccountsSpotBalances
	);

	const aggregateAccountBalancesRef = useRef<AccountSpotBalance[]>([]);

	const getAndSetAggregateAccountBalances = () => {
		const newAggregateAccountsSpotBalances = getAggregateAccountsSpotBalances();
		const filteredBalances = newAggregateAccountsSpotBalances.filter(
			(balance) =>
				balanceType ? matchEnum(balance.balanceType, balanceType) : true
		);

		if (
			!checkIfAccountsSpotBalancesAreEqual(
				filteredBalances,
				aggregateAccountBalancesRef.current
			)
		) {
			aggregateAccountBalancesRef.current = filteredBalances;
		}
	};

	useUiUpdateInterval(getAndSetAggregateAccountBalances, true, false);

	return aggregateAccountBalancesRef.current;
};

export default useAccountSpotBalances;
